import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { ACTIVITY_CODES } from '../lib/helpers';
import { pibError, mbError, digitsOnly } from '../lib/validation';
import CompanyLookup from './CompanyLookup';
import OwnAccounts from './OwnAccounts';
import { foreignFields, splitAddress, ownerFromName } from '../lib/banks';
import Icon from './Icon';
import LangSwitch from './LangSwitch';

// ============================================================
// Prvi ulazak novog korisnika i "Dodaj novu firmu"
//  mode="first": 1) dobrodošlica  2) podaci firme  -> Početna sa "Prvim koracima"
//  mode="add":   odmah podaci firme (iz liste naloga), sa dugmetom Otkaži
// ============================================================

const EMPTY = {
  company_name: '', owner_name: '', pib: '', mb: '', registration_date: '',
  activity_code: '', activity_name: '', street: '', street_number: '', postal_code: '', city: '',
  account_dom: '', account_ino: '', swift: '', iban_bank: '', email: '', phone: '',
};

export default function OnboardingPage({ mode = 'first', onCancel }) {
  const { user, signOut } = useAuth();
  const { createAgency, selectAgency, refreshAgencies } = useAgency();
  const { tr } = useI18n();
  const L = tr;
  const [step, setStep] = useState(mode === 'first' ? 'welcome' : 'form');
  const [form, setForm] = useState({ ...EMPTY, email: mode === 'first' ? (user?.email || '') : '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [regAccounts, setRegAccounts] = useState([]);

  // PIB-ovi firmi koje korisnik već ima — ista firma (isti PIB) ne može dva puta na istom nalogu
  const [ownPibs, setOwnPibs] = useState([]);
  useEffect(() => {
    supabase.from('agency_profile').select('pib, company_name')
      .then(({ data }) => setOwnPibs((data || []).filter((p) => digitsOnly(p.pib)).map((p) => ({ pib: digitsOnly(p.pib), name: p.company_name }))));
  }, []);
  const duplicate = ownPibs.find((p) => p.pib === digitsOnly(form.pib));
  const dupErr = duplicate ? L(`Firma sa ovim PIB-om već postoji na vašem nalogu (${duplicate.name || 'bez naziva'}). Izaberite je sa liste.`,
    `A company with this tax ID already exists on your account (${duplicate.name || 'no name'}). Choose it from the list.`,
    `Фирма с этим ИНН уже есть в вашем аккаунте (${duplicate.name || ''}).`) : '';

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pibErr = pibError(form.pib) || dupErr;
  const mbErr = mbError(form.mb);
  const pibOk = digitsOnly(form.pib).length === 9 && !pibErr;
  const mbOk = digitsOnly(form.mb).length === 8 && !mbErr;

  // "Popuni sa slike": podaci pročitani sa rešenja/APR izvoda

  // Podaci iz registra (po PIB-u): naziv, MB, adresa (ulica i broj), mesto, delatnost, računi;
  // ime preduzetnika iz naziva "Ime Prezime PR ...". IBAN samo ako registar vrati devizni račun;
  // inače ga korisnik upisuje iz instrukcija banke (ili bira račun ispod).
  const fromRegistry = (c, accounts, account, accountFx) => {
    setRegAccounts(accounts || []);
    const fxAcc = accountFx ? (accounts || []).find((a) => a.account === accountFx) : null;
    setForm((f) => {
      const { street, street_number: streetNumber } = splitAddress(c.address);
      return {
        ...f,
        company_name: c.name || f.company_name,
        owner_name: f.owner_name || ownerFromName(c.name),
        mb: c.mb ? digitsOnly(c.mb) : f.mb,
        street: c.address ? street : f.street,
        street_number: c.address ? streetNumber : f.street_number,
        postal_code: c.postal_code || f.postal_code,
        city: c.city || f.city,
        activity_code: c.activity_code || f.activity_code,
        activity_name: c.activity_code ? (c.activity_name || '') : f.activity_name,
        account_dom: f.account_dom || account || '',
        ...(!f.account_ino && fxAcc ? foreignFields(fxAcc) : {}),
      };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company_name.trim()) { setError(L('Upišite pun naziv firme.', 'Enter the full company name.', 'Введите полное название.')); return; }
    if (pibErr || mbErr) { setError(pibErr || mbErr); return; }
    setSaving(true);
    const agency = await createAgency(form.company_name.trim());
    if (!agency) {
      setSaving(false);
      setError(L('Firma nije sačuvana. Pokušajte ponovo.', 'The company was not saved. Please try again.', 'Не удалось сохранить.'));
      return;
    }
    const payload = {
      ...form,
      agency_id: agency.id,
      company_name: form.company_name.trim(),
      pib: digitsOnly(form.pib),
      mb: digitsOnly(form.mb),
      registration_date: form.registration_date || null,
      name_display: 'full',
      onboarding_complete: true,
    };
    const { error: profErr } = await supabase.from('agency_profile').upsert(payload, { onConflict: 'agency_id' });
    if (profErr) {
      // Podaci firme nisu sačuvani — ukloni upravo napravljen prazan nalog, da ne ostane firma bez podataka
      await supabase.from('agencies').delete().eq('id', agency.id);
      setSaving(false);
      setError(/agency_profile_unique_pib/.test(profErr.message || '')
        ? L('Firma sa ovim PIB-om već postoji na vašem nalogu. Izaberite je sa liste.', 'A company with this tax ID already exists on your account.', 'Фирма с этим ИНН уже есть.')
        : profErr.message);
      refreshAgencies();
      return;
    }
    setSaving(false);
    selectAgency(agency.id);
  };

  // ---------- 1. Dobrodošlica ----------
  if (step === 'welcome') {
    const firstName = String(user?.user_metadata?.full_name || '').split(' ')[0];
    const steps = [
      [L('Unesite podatke firme', 'Enter company details', 'Данные фирмы'), L('Naziv, PIB, adresa i tekući račun – idu na svaku fakturu', 'Name, tax ID, address and bank account – printed on every invoice', 'Название, ИНН, адрес и счёт – на каждом счёте')],
      [L('Dodajte logo', 'Add your logo', 'Добавьте логотип'), L('Nije obavezno, ali faktura lepše izgleda', 'Optional, but invoices look nicer', 'Необязательно, но красивее')],
      [L('Dodajte prvog klijenta', 'Add your first client', 'Первый клиент'), L('Može i slikanjem podataka firme', 'You can also fill it from a photo', 'Можно заполнить с фото')],
      [L('Napravite prvu fakturu', 'Create your first invoice', 'Первый счёт'), L('PDF i QR kod za plaćanje se prave sami', 'PDF and payment QR are created automatically', 'PDF и QR создаются сами')],
    ];
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.hero}>
            <LangSwitch style={{ position: 'absolute', top: 12, left: 12 }} />
            <button onClick={signOut} style={styles.logout}>{L('Odjava', 'Log out', 'Выход')}</button>
            <span style={styles.logo}>FinArt<br />Studio</span>
            <h1 style={styles.heroTitle}>
              {firstName ? L(`Dobro došli, ${firstName}!`, `Welcome, ${firstName}!`, `Добро пожаловать, ${firstName}!`) : L('Dobro došli!', 'Welcome!', 'Добро пожаловать!')}
            </h1>
            <p style={styles.heroText}>
              {L('Ovo je vaš nalog za fakture, KPO knjigu, limite i poreze. Za početak, četiri kratka koraka.',
                'This is your account for invoices, the KPO book, limits and taxes. Four short steps to start.',
                'Ваш аккаунт для счетов, книги КПО, лимитов и налогов. Четыре коротких шага.')}
            </p>
          </div>
          <div style={styles.body}>
            {steps.map(([title, desc], i) => (
              <div key={title} style={styles.stepRow}>
                <span style={styles.stepNum}>{i + 1}</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={styles.stepTitle}>{title}</span>
                  <span style={styles.stepDesc}>{desc}</span>
                </span>
              </div>
            ))}
            <button onClick={() => setStep('form')} style={styles.primary}>{L('Počnimo', "Let's start", 'Начнём')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- 2. Podaci firme ----------
  return (
    <div style={styles.page}>
      <form onSubmit={save} style={styles.shell}>
        <div style={{ ...styles.hero, alignItems: 'stretch', textAlign: 'left', padding: '28px 20px 20px' }}>
          <LangSwitch style={{ position: 'absolute', top: 12, right: 12 }} />
          {mode === 'first'
            ? <span style={styles.stepTag}>{L('Korak 1 od 4', 'Step 1 of 4', 'Шаг 1 из 4')}</span>
            : <button type="button" onClick={onCancel} style={styles.back}><Icon name="arrowLeft" size={16} />{L('Nazad na listu', 'Back to list', 'Назад')}</button>}
          <h1 style={{ ...styles.heroTitle, fontSize: 22 }}>
            {mode === 'first' ? L('Podaci vaše firme', 'Your company details', 'Данные вашей фирмы') : L('Nova firma', 'New company', 'Новая фирма')}
          </h1>
          <p style={styles.heroText}>
            {L('Ovi podaci idu na svaku fakturu i u KPO knjigu. Kasnije ih menjate u Više → Agencija.',
              'These details go on every invoice and in the KPO book. You can change them later in More → Agency.',
              'Эти данные попадут в каждый счёт. Изменить: Ещё → Агентство.')}
          </p>
        </div>

        <div style={styles.body}>
          {/* PIB prvo: podaci firme se preuzimaju automatski */}
          <Field label="PIB" ok={pibOk && L('✓ ispravan', '✓ valid', '✓ верно')} err={pibErr}>
            <input inputMode="numeric" value={form.pib} onChange={set('pib')} placeholder={L('Unesite PIB (9 cifara)', 'Enter your tax ID (9 digits)', 'Введите ИНН (9 цифр)')} style={styles.input} />
          </Field>
          <CompanyLookup pib={form.pib} onFound={fromRegistry} own />
          <Field label={L('Pun naziv firme (kao u APR) *', 'Full company name *', 'Полное название *')}>
            <input required value={form.company_name} onChange={set('company_name')} style={styles.input} />
          </Field>
          <Field label={L('Ime i prezime preduzetnika', 'Owner full name', 'Имя владельца')}>
            <input value={form.owner_name} onChange={set('owner_name')} style={styles.input} />
          </Field>
          <Field label={L('Matični broj', 'Registration no.', 'Рег. номер')} ok={mbOk && L('✓ ispravan', '✓ valid', '✓ верно')} err={mbErr}>
            <input inputMode="numeric" value={form.mb} onChange={set('mb')} style={styles.input} />
          </Field>
          <Field label={L('Šifra delatnosti', 'Activity code', 'Код деятельности')}>
            <select value={form.activity_code} onChange={(e) => {
              const found = ACTIVITY_CODES.find((a) => a.code === e.target.value);
              setForm((f) => ({ ...f, activity_code: e.target.value, activity_name: found?.name || '' }));
            }} style={styles.input}>
              <option value="">{L('— izaberite —', '— choose —', '— выберите —')}</option>
              {form.activity_code && !ACTIVITY_CODES.some((a) => a.code === form.activity_code) && (
                <option value={form.activity_code}>{form.activity_code} — {form.activity_name || form.activity_code}</option>
              )}
              {ACTIVITY_CODES.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </select>
          </Field>
          <Field label={L('Datum registracije', 'Registration date', 'Дата регистрации')}>
            <input type="date" value={form.registration_date} onChange={set('registration_date')} style={styles.input} />
          </Field>
          <div style={styles.grid2}>
            <Field label={L('Ulica', 'Street', 'Улица')}>
              <input value={form.street} onChange={set('street')} style={styles.input} />
            </Field>
            <Field label={L('Broj', 'No.', 'Дом')}>
              <input value={form.street_number} onChange={set('street_number')} style={styles.input} />
            </Field>
          </div>
          <div style={styles.grid2}>
            <Field label={L('Poštanski broj', 'Postal code', 'Индекс')}>
              <input inputMode="numeric" value={form.postal_code} onChange={set('postal_code')} style={styles.input} />
            </Field>
            <Field label={L('Mesto', 'City', 'Город')}>
              <input value={form.city} onChange={set('city')} style={styles.input} />
            </Field>
          </div>
          <Field label={L('Tekući račun (dinarski)', 'Bank account (RSD)', 'Расчётный счёт (RSD)')}>
            <input value={form.account_dom} onChange={set('account_dom')} placeholder="000-0000000000000-00" style={styles.input} />
          </Field>
          <Field label={L('Račun za uplate iz inostranstva (IBAN)', 'Account for payments from abroad (IBAN)', 'Счёт для оплат из-за рубежа (IBAN)')}>
            <input value={form.account_ino} onChange={set('account_ino')} placeholder="RS35 0000 0000 0000 0000 00" style={styles.input} />
          </Field>
          {regAccounts.length > 0 && !form.account_ino && (
            <div style={{ fontSize: 12, color: 'var(--ink-500)', margin: '-8px 0 12px' }}>
              {L('Upišite IBAN iz instrukcija vaše banke za priliv iz inostranstva.', 'Enter the IBAN from your bank’s instructions for incoming payments from abroad.', 'Укажите IBAN из инструкций вашего банка.')}
            </div>
          )}
          <div style={styles.grid2}>
            <Field label="SWIFT">
              <input value={form.swift} onChange={set('swift')} style={styles.input} />
            </Field>
            <Field label={L('Banka', 'Bank', 'Банк')}>
              <input value={form.iban_bank} onChange={set('iban_bank')} style={styles.input} />
            </Field>
          </div>
          <OwnAccounts
            accounts={regAccounts}
            accountDom={form.account_dom}
            accountIno={form.account_ino}
            onPickDom={(a) => setForm((f) => ({ ...f, account_dom: a.account }))}
            onPickFx={(a, fx) => setForm((f) => ({ ...f, ...fx }))}
          />
          <div style={styles.grid2}>
            <Field label="E-mail">
              <input type="email" value={form.email} onChange={set('email')} style={styles.input} />
            </Field>
            <Field label={L('Telefon', 'Phone', 'Телефон')}>
              <input value={form.phone} onChange={set('phone')} style={styles.input} />
            </Field>
          </div>

          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" disabled={saving} style={styles.primary}>
            {saving ? L('Čuvam...', 'Saving...', 'Сохраняю...') : L('Sačuvaj i nastavi', 'Save and continue', 'Сохранить')}
          </button>
          {mode === 'first' && (
            <span style={styles.note}>{L('Zatim: logo, prvi klijent i prva faktura', 'Next: logo, first client and first invoice', 'Далее: логотип, клиент и счёт')}</span>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, ok, err }) {
  return (
    <label style={styles.field}>
      <span style={styles.labelRow}>
        <span style={styles.label}>{label}</span>
        {ok && !err && <span style={styles.ok}>{ok}</span>}
      </span>
      {children}
      {err && <span style={styles.fieldErr}>{err}</span>}
    </label>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--paper)', display: 'flex', justifyContent: 'center', padding: '24px 12px' },
  shell: { width: '100%', maxWidth: 520, background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--line)', overflow: 'hidden', alignSelf: 'flex-start' },
  hero: { position: 'relative', background: 'var(--paper)', borderBottom: '1px solid var(--line)', padding: '40px 22px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 },
  logout: { position: 'absolute', top: 12, right: 12, border: 'none', background: 'rgba(255,255,255,0.7)', color: 'var(--ink-700)', borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 600 },
  logo: { width: 72, height: 72, borderRadius: '50%', background: 'var(--teal-800)', color: '#C9C0F5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, lineHeight: 1.1 },
  heroTitle: { margin: '6px 0 0', fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)' },
  heroText: { margin: 0, fontSize: 15, lineHeight: 1.5, color: 'var(--ink-700)' },
  stepTag: { fontSize: 13, fontWeight: 700, color: 'var(--teal-800)' },
  back: { alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'rgba(255,255,255,0.7)', color: 'var(--teal-800)', borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 700 },
  body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 },
  stepRow: { display: 'flex', alignItems: 'center', gap: 14, padding: 14, border: '1px solid var(--line)', borderRadius: 10 },
  stepNum: { flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: 'var(--lavender-100)', color: 'var(--lavender-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 },
  stepTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ink-900)' },
  stepDesc: { fontSize: 13, color: 'var(--ink-500)' },
  primary: { marginTop: 6, minHeight: 54, border: 'none', borderRadius: 10, background: 'var(--teal-800)', color: '#fff', fontSize: 16, fontWeight: 700 },
  note: { fontSize: 12, color: 'var(--ink-500)', textAlign: 'center' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  labelRow: { display: 'flex', justifyContent: 'space-between', gap: 8 },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--ink-700)' },
  ok: { fontSize: 12, fontWeight: 700, color: 'var(--forest-600)' },
  fieldErr: { fontSize: 12, color: 'var(--red-600)' },
  input: { border: '1.5px solid var(--line)', borderRadius: 8, padding: '11px 13px', fontSize: 15, width: '100%', minHeight: 46, background: 'var(--surface)' },
  grid2: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 },
  error: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13 },
};
